import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Legend } from "recharts";
import { Wallet, Landmark, Coins, TrendingUp, TrendingDown, ShoppingCart, HandCoins, Users, Plus, ArrowRight, AlertTriangle } from "lucide-react";
import { DueAlerts } from "@/components/DueAlerts";

const KPI = ({ icon: Icon, label, value, tone = "default", testid }) => {
  const tones = {
    default: "text-primary bg-primary/10",
    success: "text-emerald-500 bg-emerald-500/10",
    danger: "text-red-500 bg-red-500/10",
    warn: "text-amber-500 bg-amber-500/10",
    info: "text-blue-500 bg-blue-500/10",
  };
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">{label}</div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon size={17} /></div>
      </div>
      <div className="text-2xl font-bold num text-foreground">{value}</div>
    </div>
  );
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="text-center py-20 text-muted-foreground">Yükleniyor...</div>;

  const chartTicks = d.days_chart.map((x) => ({ ...x, day: new Date(x.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" }) }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Genel Bakış</h1>
          <p className="text-sm text-muted-foreground mt-1">İşletmenizin canlı finansal durumu</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => nav("/satislar/yeni")} data-testid="quick-new-sale-btn"><Plus size={16} className="mr-1" /> Yeni Satış</Button>
          <Button variant="secondary" onClick={() => nav("/tahsilatlar?new=1")} data-testid="quick-collect-btn"><HandCoins size={16} className="mr-1" /> Ödeme Al</Button>
          <Button variant="outline" onClick={() => nav("/musteriler?new=1")} data-testid="quick-new-customer-btn"><Users size={16} className="mr-1" /> Yeni Müşteri</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <KPI icon={Wallet} label="Toplam Nakit" value={fmtTL(d.total_nakit)} testid="kpi-total-nakit" />
        <KPI icon={Landmark} label="Bankalar" value={fmtTL(d.total_bank)} tone="info" testid="kpi-total-bank" />
        <KPI icon={Coins} label="Kasalar" value={fmtTL(d.total_cash)} tone="warn" testid="kpi-total-cash" />
        <KPI icon={TrendingUp} label="Toplam Alacak" value={fmtTL(d.total_receivable)} tone="danger" testid="kpi-total-receivable" />
        <KPI icon={ShoppingCart} label="Bugünkü Satış" value={fmtTL(d.today_sales)} tone="success" testid="kpi-today-sales" />
        <KPI icon={HandCoins} label="Bugünkü Tahsilat" value={fmtTL(d.today_collections)} tone="success" testid="kpi-today-collections" />
        <KPI icon={TrendingUp} label="Bu Ay Gelir" value={fmtTL(d.month_income)} tone="success" testid="kpi-month-income" />
        <KPI icon={TrendingDown} label="Bu Ay Gider" value={fmtTL(d.month_expense)} tone="danger" testid="kpi-month-expense" />
        <KPI icon={Wallet} label="Bu Ay Net" value={fmtTL(d.month_net)} tone={d.month_net >= 0 ? "success" : "danger"} testid="kpi-month-net" />
        <KPI icon={ShoppingCart} label="Bu Ay Satış" value={fmtTL(d.month_sales)} testid="kpi-month-sales" />
      </div>

      <DueAlerts />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">Satış ve Tahsilat (14 Gün)</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={chartTicks}>
                <defs>
                  <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(11 71% 58%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(11 71% 58%)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(160 84% 39%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(160 84% 39%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                  formatter={(v) => fmtTL(v)} />
                <Legend />
                <Area type="monotone" name="Satış" dataKey="sales" stroke="hsl(11 71% 58%)" fill="url(#sg)" strokeWidth={2} />
                <Area type="monotone" name="Tahsilat" dataKey="collections" stroke="hsl(160 84% 39%)" fill="url(#cg)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">En Borçlu Müşteriler</h3>
            <Link to="/musteriler" className="text-xs text-primary hover:underline flex items-center gap-1">Tümü <ArrowRight size={12} /></Link>
          </div>
          <div className="space-y-2" data-testid="top-debtors-list">
            {d.top_debtors.filter((x) => x.balance > 0).slice(0, 6).map((c) => (
              <Link key={c.id} to={`/musteriler/${c.id}`}
                className="flex items-center justify-between p-2.5 rounded hover:bg-accent transition-colors">
                <span className="font-medium text-sm truncate">{c.name}</span>
                <span className="num font-semibold text-primary">{fmtTL(c.balance)}</span>
              </Link>
            ))}
            {d.top_debtors.filter((x) => x.balance > 0).length === 0 && (
              <div className="text-center py-6 text-sm text-muted-foreground">Borçlu müşteri yok</div>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-bold text-lg mb-4">Son İşlemler</h3>
          <div className="space-y-1.5" data-testid="recent-activity-list">
            {d.recent.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${r.type === "satis" ? "bg-primary" : "bg-emerald-500"}`} />
                  <div>
                    <div className="font-medium">{r.type === "satis" ? "Satış" : "Tahsilat"}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(r.date)}</div>
                  </div>
                </div>
                <span className={`num font-semibold ${r.type === "satis" ? "text-primary" : "text-emerald-500"}`}>{fmtTL(r.amount)}</span>
              </div>
            ))}
            {d.recent.length === 0 && <div className="text-center py-6 text-sm text-muted-foreground">Henüz işlem yok</div>}
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-amber-500" />
            <h3 className="font-bold text-lg">Kritik Stok</h3>
          </div>
          <div className="space-y-2">
            {d.low_stock.length ? d.low_stock.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm p-2.5 rounded bg-amber-500/5 border border-amber-500/20">
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">Min: {p.min_stock}</div>
                </div>
                <span className="num font-semibold text-amber-500">{p.stock}</span>
              </div>
            )) : <div className="text-center py-6 text-sm text-muted-foreground">Kritik stok yok</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
